import SEOHead from '../../components/seo/SEOHead';

export default function Privacy() {
  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <SEOHead
        title="Privacy Policy | DBD Podcast"
        description="DBD Podcast Privacy Policy. Learn how we collect, use, store, and protect your personal data in compliance with GDPR and other privacy regulations."
        keywords="privacy policy, GDPR compliance, data protection, personal data, podcast privacy"
        ogTitle="Privacy Policy | DBD Podcast"
        ogDescription="DBD Podcast Privacy Policy. Learn how we collect, use, store, and protect your personal data."
        ogType="website"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/privacy`}
        noindex={false}
      />

      {/* Header */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
            <p className="text-gray-600 text-lg">
              Effective Date: September 8, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                
                <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-6">
                  We are committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy explains how we collect, use, store, and share your personal information when you use our website. It also describes your rights under the General Data Protection Regulation (GDPR) and how you can exercise them.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Data Controller</h2>
                <p className="text-gray-700 mb-2">
                  The data controller responsible for your personal data is:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-gray-700 font-medium">AliasSmith AB</p>
                  <p className="text-gray-700">Upplandsgatan 49</p>
                  <p className="text-gray-700">Email: info@aliassmith.se</p>
                  <p className="text-gray-700">Phone: +468309855</p>
                </div>
                <p className="text-gray-700 mb-6">
                  If you have any questions about this policy or your data, you can contact our Data Protection Officer at: <a href="mailto:legal@aliassmith.se" className="text-amber-600 hover:text-amber-700 transition-colors">legal@aliassmith.se</a>
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">3. What Personal Data We Collect</h2>
                <p className="text-gray-700 mb-4">
                  We collect the following categories of personal data:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li>Full Name</li>
                  <li>Country of Residence</li>
                  <li>Email Address</li>
                  <li>Industry Represented</li>
                  <li>Viewing Behavior: Information about what you watch on our site and how many times you view each item</li>
                </ul>
                <p className="text-gray-700 mb-6">
                  We do not collect sensitive personal data (such as health, political, or religious information) unless explicitly stated.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">4. How We Collect Your Data</h2>
                <p className="text-gray-700 mb-4">
                  We collect your data in the following ways:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li><strong>Directly from you:</strong> When you register, fill out forms, or communicate with us.</li>
                  <li><strong>Automatically:</strong> Through cookies and similar tracking technologies when you use our website (see Section 10).</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Purposes and Legal Basis for Processing</h2>
                <p className="text-gray-700 mb-4">
                  We process your personal data for the following purposes and on the following legal bases:
                </p>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse border border-gray-300 rounded-lg">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Purpose</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Legal Basis (GDPR Art. 6)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">To create and manage your account</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">Consent; Contractual necessity</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">To communicate with you (e.g., updates)</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">Consent; Legitimate interests</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">To analyze and improve our services</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">Consent (for analytics/tracking)</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">To track your viewing behavior</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">Consent (explicit, for tracking)</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">To comply with legal obligations</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">Legal obligation</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-gray-700 mb-6">
                  We will always seek your explicit, informed consent for tracking activities and for any processing not strictly necessary for providing our services.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">6. How We Use Your Data</h2>
                <p className="text-gray-700 mb-4">
                  We use your data to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li>Provide and maintain our podcast platform services</li>
                  <li>Create and manage your user account</li>
                  <li>Track your viewing preferences and episode engagement</li>
                  <li>Send you relevant updates about new episodes and platform features</li>
                  <li>Analyze usage patterns to improve our content and user experience</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Comply with legal and regulatory requirements</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Data Sharing and Third Parties</h2>
                <p className="text-gray-700 mb-4">
                  We may share your personal data with:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li><strong>Service Providers:</strong> Third-party companies that help us operate our platform (hosting, analytics, email services)</li>
                  <li><strong>Legal Authorities:</strong> When required by law or to protect our legal rights</li>
                  <li><strong>Business Partners:</strong> With your explicit consent for promotional or collaborative purposes</li>
                </ul>
                <p className="text-gray-700 mb-6">
                  We do not sell your personal data to third parties for marketing purposes.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Data Retention</h2>
                <p className="text-gray-700 mb-6">
                  We retain your personal data only as long as necessary for the purposes outlined in this policy. Account data is typically retained for the duration of your account plus 3 years after closure. Viewing behavior data is anonymized after 2 years unless you have consented to longer retention for analytics purposes.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Your Rights Under GDPR</h2>
                <p className="text-gray-700 mb-4">
                  You have the following rights regarding your personal data:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                  <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                  <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for consent-based processing</li>
                </ul>
                <p className="text-gray-700 mb-6">
                  To exercise these rights, contact us at <a href="mailto:legal@aliassmith.se" className="text-amber-600 hover:text-amber-700 transition-colors">legal@aliassmith.se</a>
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar technologies to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li>Maintain your login session</li>
                  <li>Remember your preferences</li>
                  <li>Track episode viewing for analytics</li>
                  <li>Improve website performance</li>
                </ul>
                <p className="text-gray-700 mb-6">
                  You can manage cookie preferences through your browser settings. Note that disabling certain cookies may affect website functionality.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Data Security</h2>
                <p className="text-gray-700 mb-6">
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">12. International Data Transfers</h2>
                <p className="text-gray-700 mb-6">
                  Your data may be processed in countries outside the European Economic Area. When we transfer data internationally, we ensure adequate protection through approved mechanisms such as Standard Contractual Clauses or adequacy decisions.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">13. Children's Privacy</h2>
                <p className="text-gray-700 mb-6">
                  Our services are not intended for children under 16 years of age. We do not knowingly collect personal data from children under 16. If you become aware that a child has provided us with personal data, please contact us immediately.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">14. Changes to This Policy</h2>
                <p className="text-gray-700 mb-6">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the effective date. Your continued use of our services after changes constitutes acceptance of the updated policy.
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">15. Complaints</h2>
                <p className="text-gray-700 mb-6">
                  If you believe we have not handled your personal data in accordance with this policy, you have the right to file a complaint with your local data protection authority. In Sweden, this is the Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten).
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">16. Contact Information</h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-2">
                    For any questions about this Privacy Policy or your personal data, please contact:
                  </p>
                  <div className="text-gray-700">
                    <p className="font-medium">AliasSmith AB</p>
                    <p>Upplandsgatan 49</p>
                    <p>Email: <a href="mailto:legal@aliassmith.se" className="text-amber-600 hover:text-amber-700 transition-colors">legal@aliassmith.se</a></p>
                    <p>Phone: +468309855</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
